import React, { FC, useCallback } from 'react';

import { useRouter } from 'expo-router';
import { FlatList, FlatListProps } from 'react-native';

import DrawerPage from 'components/layout/drawer-page';
import { RefreshButton } from 'components/refresh-button';
import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import { UserOrdersDefaultLimit } from 'hooks/use-user-orders';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import { t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';
import { routes } from 'utils/routing';

import MinimalOrder from './minimal-order';
import OrdersEmpty from './orders-empty';
import { OfferDisclaimer, OrdersEmptyWrapper } from './styled';
import { MinimalAccountOrder } from './types';
import { useMinimalAccountOrders } from './use-minimal-account-orders';

function minimalOrderKeyExtractor(item: MinimalAccountOrder, index: number) {
  return item?.rbiOrderId || `${index}_minimal_order`;
}

export const MinimalAccountOrders: FC = () => {
  const { unavailableCartEntries } = useOrderContext();
  const { isLoading, orders, refetch } = useMinimalAccountOrders(
    true,
    { limit: UserOrdersDefaultLimit },
    'no-cache'
  );

  const router = useRouter();

  const onRefreshPress = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleUnavailableModalDismiss = useCallback(() => {
    router.replace(routes.cart);
  }, [router]);

  const refreshButton = <RefreshButton onPress={onRefreshPress}>{t('refresh')}</RefreshButton>;
  const hasOrders = !!orders.length;

  const renderHistoricalOrder = useCallback<
    NonNullable<FlatListProps<MinimalAccountOrder>['renderItem']>
  >(({ item }) => <MinimalOrder order={item} />, []);

  if (unavailableCartEntries.length > 0) {
    return (
      <RemoveUnavailableItemsModal
        onConfirmCb={handleUnavailableModalDismiss}
        unavailableCartEntries={unavailableCartEntries}
      />
    );
  }

  function getContent() {
    if (isLoading) {
      return <LoyaltyLoader />;
    }

    if (hasOrders) {
      return (
        <FlatList
          testID="inner-layout"
          ListHeaderComponent={
            <>
              <OfferDisclaimer>{t('recentOrdersDisclaimer')}</OfferDisclaimer>
              {refreshButton}
            </>
          }
          keyExtractor={minimalOrderKeyExtractor}
          data={orders}
          renderItem={renderHistoricalOrder}
        />
      );
    }

    return (
      <OrdersEmptyWrapper>
        <OrdersEmpty>{refreshButton}</OrdersEmpty>
      </OrdersEmptyWrapper>
    );
  }

  return <DrawerPage headerText={t('recentOrders')}>{getContent()}</DrawerPage>;
};
